import { Database, MoreHorizontal, Webhook } from "lucide-react";
import { DropdownMenu as RadixDropdownMenu } from "radix-ui";
import { useState } from "react";
import { useHistory } from "react-router-dom";

import { DataConnector, DataConnectorType } from "@/api/data-connectors";
import { Badge } from "@/design-system/components/Badge/Badge";

const TYPE_ICON: Record<DataConnectorType, typeof Webhook> = {
  api: Webhook,
  airbyte: Database,
};

const TYPE_LABEL: Record<DataConnectorType, string> = {
  api: "API",
  airbyte: "Airbyte",
};

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

type MenuAction = {
  label: string;
  onClick: () => void;
  destructive?: boolean;
};

type Props = {
  dataConnector: DataConnector;
  onEdit?: (dc: DataConnector) => void;
  onSetStatus?: (dc: DataConnector, status: "active" | "disabled") => void;
};

export function DataConnectorCard({
  dataConnector,
  onEdit,
  onSetStatus,
}: Props) {
  const history = useHistory();
  const [isHovered, setIsHovered] = useState(false);

  const Icon = TYPE_ICON[dataConnector.type];
  const isDisabled = dataConnector.status === "disabled";

  const handleCardClick = () => {
    history.push(`/destinations/data-connectors/${dataConnector.id}`);
  };

  const menuActions: MenuAction[] = [];
  if (onEdit) {
    menuActions.push({
      label: "Edit",
      onClick: () => onEdit(dataConnector),
    });
  }
  if (onSetStatus) {
    menuActions.push(
      isDisabled
        ? {
            label: "Enable",
            onClick: () => onSetStatus(dataConnector, "active"),
          }
        : {
            label: "Disable",
            onClick: () => onSetStatus(dataConnector, "disabled"),
          },
    );
  }

  return (
    <div className="group relative">
      <div
        tabIndex={0}
        aria-label={`${dataConnector.name || "Untitled"}. Open data connector details.`}
        className={`focus-visible:ring-interactive-selected flex min-h-[90px] cursor-pointer items-center gap-5 rounded-xl border border-solid px-[21px] py-[17px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${isHovered ? "bg-surface-sunken border-gray-400" : "border-borderSubtle bg-surface-default"} ${isDisabled ? "opacity-60" : ""}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleCardClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleCardClick();
          }
        }}
      >
        <div className="bg-surface-selected text-textActionSelected flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
          <Icon className="h-6 w-6" strokeWidth={1.75} aria-hidden />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span
            className={`text-textDefault text-left text-sm leading-5 font-semibold ${
              isHovered ? "underline underline-offset-2" : "no-underline"
            }`}
          >
            {dataConnector.name || (
              <span className="text-iconMinimal italic">Untitled</span>
            )}
          </span>
          <span className="text-textSubtle text-xs leading-4 font-normal">
            {TYPE_LABEL[dataConnector.type]} · Created{" "}
            {formatRelativeDate(dataConnector.createdAt)}
          </span>
        </div>

        <Badge
          variant={isDisabled ? "unknown" : "success"}
          text={isDisabled ? "Disabled" : "Active"}
        />

        {menuActions.length > 0 && (
          <RadixDropdownMenu.Root>
            <RadixDropdownMenu.Trigger asChild>
              <button
                type="button"
                tabIndex={isHovered ? 0 : -1}
                onClick={(e) => e.stopPropagation()}
                className={`text-textDefault flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded transition-opacity hover:bg-black/5 ${
                  isHovered ? "opacity-100" : "pointer-events-none opacity-0"
                }`}
                aria-label="More actions"
              >
                <MoreHorizontal className="h-6 w-6" strokeWidth={1.5} />
              </button>
            </RadixDropdownMenu.Trigger>
            <RadixDropdownMenu.Portal>
              <RadixDropdownMenu.Content
                align="end"
                sideOffset={4}
                className="z-popover border-interactive-selected bg-surface-default min-w-[160px] rounded-md border p-2 shadow-lg"
              >
                {menuActions.map((action) => (
                  <RadixDropdownMenu.Item
                    key={action.label}
                    onClick={(e) => e.stopPropagation()}
                    onSelect={(e) => {
                      e.preventDefault();
                      action.onClick();
                    }}
                    className={`cursor-pointer rounded-md px-3 py-2 text-sm transition-colors outline-none ${
                      action.destructive
                        ? "text-textError hover:bg-status-error-tint focus:bg-status-error-tint"
                        : "text-textDefault hover:bg-surface-selected focus:bg-surface-selected"
                    }`}
                  >
                    {action.label}
                  </RadixDropdownMenu.Item>
                ))}
              </RadixDropdownMenu.Content>
            </RadixDropdownMenu.Portal>
          </RadixDropdownMenu.Root>
        )}
      </div>
    </div>
  );
}
